import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import DashboardShell from "@/components/dashboard/DashboardShell";
import api, { formatApiErrorDetail, BACKEND_URL } from "@/lib/api";
import {
  Wand2, FileText, Film, ImageIcon, Mic, Layers, Loader2,
  Check, RefreshCw, ChevronRight, Plus, Trash2, Save, AlertTriangle,
} from "lucide-react";

const STAGES = [
  { id: "brief",      label: "Brief",      icon: FileText },
  { id: "script",     label: "Script",     icon: Wand2 },
  { id: "storyboard", label: "Storyboard", icon: Layers },
  { id: "keyframes",  label: "Keyframes",  icon: ImageIcon },
  { id: "voiceover",  label: "Voiceover",  icon: Mic },
  { id: "assemble",   label: "Assemble",   icon: Film },
];

export default function DirectorPage() {
  const { user, loading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [stage, setStage] = useState("brief");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  // Brief form fields
  const [form, setForm] = useState({
    title: "", goal: "", audience: "", tone: "cinematic",
    aspect: "9:16", total_duration: 20, shot_count: 4,
  });

  useEffect(() => {
    if (!user) return;
    api.get("/director/projects").then(({ data }) => setProjects(data.projects || []));
    const pid = searchParams.get("project");
    if (pid) loadProject(pid);
  }, [user]);

  const loadProject = async (pid) => {
    setError("");
    try {
      const { data } = await api.get(`/director/projects/${pid}`);
      setProject(data);
      setStage(data.current_stage || "brief");
      setForm({ ...form, ...(data.brief || {}) });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const startNew = () => {
    setProject(null);
    setStage("brief");
    setForm({ title: "", goal: "", audience: "", tone: "cinematic", aspect: "9:16", total_duration: 20, shot_count: 4 });
    setSearchParams({});
  };

  const saveBrief = async () => {
    setError(""); setBusy("brief");
    try {
      const payload = { ...form, total_duration: Number(form.total_duration), shot_count: Number(form.shot_count) };
      if (project?.project_id) payload.project_id = project.project_id;
      const { data } = await api.post("/director/projects", payload);
      setProject(data.project);
      setSearchParams({ project: data.project.project_id });
      setStage("script");
      const refreshed = await api.get("/director/projects");
      setProjects(refreshed.data.projects || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const runStage = async (s) => {
    if (!project) return;
    setError(""); setBusy(s);
    try {
      const { data } = await api.post(`/director/projects/${project.project_id}/${s}`);
      setProject(data.project);
      const next = nextStage(s);
      if (next) setStage(next);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const renderKeyframe = async (shotId) => {
    setError(""); setBusy(`shot-${shotId}`);
    try {
      const { data } = await api.post(`/director/projects/${project.project_id}/keyframes/${shotId}`);
      setProject(data.project);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const onEditScript = async (next) => {
    setError(""); setBusy("script-save");
    try {
      const { data } = await api.put(`/director/projects/${project.project_id}/script`, next);
      setProject(data.project);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const onDeleteProject = async (pid) => {
    if (!confirm("Delete this project? This can't be undone.")) return;
    await api.delete(`/director/projects/${pid}`);
    if (project?.project_id === pid) startNew();
    const refreshed = await api.get("/director/projects");
    setProjects(refreshed.data.projects || []);
  };

  if (loading || user === undefined) return null;
  if (!user) return <Navigate to="/login?next=/director" replace />;

  const stages = project?.stages || {};

  return (
    <DashboardShell>
      <div className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-3">Director · Creative Agent</p>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Brief → Script → Storyboard → Render</h1>
        <p className="text-[#A0A0A0] mt-4 max-w-2xl">Walk a project through every creative stage. Edit at any step, regenerate downstream stages, save progress automatically.</p>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Project sidebar */}
        <div className="lg:col-span-1 space-y-3">
          <button
            onClick={startNew}
            data-testid="director-new-project"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-4 py-3 text-sm hover:bg-[#F0C84B]"
          >
            <Plus className="w-4 h-4" /> New Project
          </button>
          <div className="border border-[#2A2A2A] bg-[#0F0F0F]" data-testid="director-project-list">
            <div className="px-4 py-3 border-b border-[#2A2A2A] text-[10px] uppercase tracking-[0.22em] text-[#A0A0A0]">Your projects</div>
            {projects.length === 0 ? (
              <div className="px-4 py-6 text-xs text-[#5A5A5A] text-center">No projects yet.</div>
            ) : (
              <div className="divide-y divide-[#1A1A1A]">
                {projects.map((p) => (
                  <button
                    key={p.project_id}
                    onClick={() => loadProject(p.project_id)}
                    data-testid={`director-project-${p.project_id}`}
                    className={`w-full text-left px-4 py-3 hover:bg-[#121212] transition-colors flex items-center justify-between gap-2 ${project?.project_id === p.project_id ? "bg-[#121212] border-l-2 border-[#D4AF37]" : ""}`}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#F4F4F0] truncate">{p.title}</p>
                      <p className="text-[10px] text-[#A0A0A0] mt-0.5 uppercase tracking-wider">{p.current_stage}</p>
                    </div>
                    <Trash2
                      onClick={(e) => { e.stopPropagation(); onDeleteProject(p.project_id); }}
                      className="w-3.5 h-3.5 text-[#5A5A5A] hover:text-red-400 flex-shrink-0"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Main pane */}
        <div className="lg:col-span-3">
          {/* Stage rail */}
          <div className="mb-6 flex items-center gap-1 overflow-x-auto" data-testid="director-stages">
            {STAGES.map((s, i) => {
              const st = stages[s.id]?.status;
              const done = st === "succeeded";
              const failed = st === "failed";
              const active = stage === s.id;
              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => project ? setStage(s.id) : null}
                    disabled={!project && s.id !== "brief"}
                    data-testid={`director-stage-${s.id}`}
                    className={`flex items-center gap-2 px-3 py-2 text-[10px] uppercase tracking-wider border whitespace-nowrap transition-colors disabled:opacity-40 ${
                      active ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/5"
                        : done ? "border-emerald-500/40 text-emerald-300"
                        : failed ? "border-red-500/40 text-red-300"
                        : "border-[#2A2A2A] text-[#A0A0A0] hover:text-[#F4F4F0]"
                    }`}
                  >
                    <s.icon className="w-3 h-3" />
                    {s.label}
                    {done && <Check className="w-3 h-3" />}
                  </button>
                  {i < STAGES.length - 1 && <ChevronRight className="w-3 h-3 text-[#3A3A3A] flex-shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>

          {error && (
            <div className="mb-4 px-3 py-2 border border-red-500/40 bg-red-500/10 text-red-300 text-xs flex items-center gap-2" data-testid="director-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </div>
          )}

          {/* Stage body */}
          {stage === "brief" && (
            <BriefStage form={form} setForm={setForm} onSubmit={saveBrief} busy={busy === "brief"} />
          )}
          {stage === "script" && project && (
            <ScriptStage project={project} onGenerate={() => runStage("script")} onEdit={onEditScript} busy={busy === "script"} />
          )}
          {stage === "storyboard" && project && (
            <StoryboardStage project={project} onGenerate={() => runStage("storyboard")} busy={busy === "storyboard"} />
          )}
          {stage === "keyframes" && project && (
            <KeyframesStage project={project} onRenderShot={renderKeyframe} busy={busy} />
          )}
          {stage === "voiceover" && project && (
            <VoiceoverStage project={project} onGenerate={() => runStage("voiceover")} busy={busy === "voiceover"} />
          )}
          {stage === "assemble" && project && (
            <AssembleStage project={project} onGenerate={() => runStage("assemble")} busy={busy === "assemble"} />
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

function nextStage(s) {
  const order = ["brief", "script", "storyboard", "keyframes", "voiceover", "assemble"];
  const i = order.indexOf(s);
  return i >= 0 && i < order.length - 1 ? order[i + 1] : null;
}

/* ───── Stages ───── */

function BriefStage({ form, setForm, onSubmit, busy }) {
  const set = (k, v) => setForm({ ...form, [k]: v });
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="director-brief-stage">
      <h3 className="font-display text-xl font-bold mb-4">Tell me about the project</h3>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Project title" value={form.title} onChange={(v) => set("title", v)} testId="brief-title" />
        <Field label="Tone" value={form.tone} onChange={(v) => set("tone", v)} testId="brief-tone" />
        <div className="md:col-span-2">
          <Field label="Goal — what should this video do?" value={form.goal} onChange={(v) => set("goal", v)} multiline testId="brief-goal" />
        </div>
        <div className="md:col-span-2">
          <Field label="Audience (optional)" value={form.audience} onChange={(v) => set("audience", v)} testId="brief-audience" />
        </div>
        <Field label="Aspect" value={form.aspect} onChange={(v) => set("aspect", v)} type="select" options={["9:16", "1:1", "16:9"]} testId="brief-aspect" />
        <Field label="Total duration (s)" value={form.total_duration} onChange={(v) => set("total_duration", v)} type="number" testId="brief-duration" />
        <Field label="Number of shots" value={form.shot_count} onChange={(v) => set("shot_count", v)} type="number" testId="brief-shots" />
      </div>
      <button
        onClick={onSubmit}
        disabled={busy || !form.title || form.goal.length < 4}
        data-testid="director-save-brief"
        className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-5 py-2.5 text-sm hover:bg-[#F0C84B] disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        Save Brief & Continue
      </button>
    </div>
  );
}

function ScriptStage({ project, onGenerate, onEdit, busy }) {
  const script = project.stages?.script?.data || null;
  const failed = project.stages?.script?.status === "failed";
  const [draft, setDraft] = useState(null);

  useEffect(() => { setDraft(script); }, [project.project_id, project.stages?.script?.updated_at]);

  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="director-script-stage">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">Shooting script</h3>
        <button
          onClick={onGenerate} disabled={busy}
          data-testid="director-generate-script"
          className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {script ? "Regenerate" : "Generate"}
        </button>
      </div>
      {!script && !busy && <p className="text-sm text-[#A0A0A0]">Click Generate to draft a beat-by-beat script from your brief. Uses ~2 credits.</p>}
      {failed && <p className="text-sm text-red-300">The last attempt failed — credits auto-refunded. Try again.</p>}
      {script && draft && (
        <div className="space-y-4">
          <Field label="Logline" value={draft.logline || ""} onChange={(v) => setDraft({ ...draft, logline: v })} testId="script-logline" />
          <Field label="Voiceover script" value={draft.voiceover || ""} onChange={(v) => setDraft({ ...draft, voiceover: v })} multiline testId="script-vo" />
          <p className="text-[10px] uppercase tracking-wider text-[#A0A0A0]">Shots</p>
          {(draft.shots || []).map((s, idx) => (
            <div key={s.id || idx} className="border border-[#1A1A1A] bg-[#0A0A0A] p-3 grid md:grid-cols-12 gap-2 text-xs">
              <span className="md:col-span-1 text-[#D4AF37] font-mono">#{s.id || idx + 1}</span>
              <input
                type="number" value={s.duration || 0}
                onChange={(e) => {
                  const shots = [...draft.shots]; shots[idx] = { ...s, duration: Number(e.target.value) };
                  setDraft({ ...draft, shots });
                }}
                className="md:col-span-1 bg-[#0F0F0F] border border-[#2A2A2A] px-2 py-1 text-[#F4F4F0]"
              />
              <input
                value={s.action || ""}
                onChange={(e) => {
                  const shots = [...draft.shots]; shots[idx] = { ...s, action: e.target.value };
                  setDraft({ ...draft, shots });
                }}
                placeholder="Action"
                className="md:col-span-4 bg-[#0F0F0F] border border-[#2A2A2A] px-2 py-1 text-[#F4F4F0]"
              />
              <input
                value={s.image_prompt || ""}
                onChange={(e) => {
                  const shots = [...draft.shots]; shots[idx] = { ...s, image_prompt: e.target.value };
                  setDraft({ ...draft, shots });
                }}
                placeholder="Image prompt"
                className="md:col-span-6 bg-[#0F0F0F] border border-[#2A2A2A] px-2 py-1 text-[#F4F4F0]"
              />
            </div>
          ))}
          <button
            onClick={() => onEdit(draft)}
            disabled={busy}
            className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-[#2A2A2A] hover:border-[#D4AF37] text-[#A0A0A0] hover:text-[#F4F4F0] disabled:opacity-50"
            data-testid="director-save-script"
          >
            <Save className="w-3.5 h-3.5" /> Save script edits
          </button>
        </div>
      )}
    </div>
  );
}

function StoryboardStage({ project, onGenerate, busy }) {
  const sb = project.stages?.storyboard?.data;
  const script = project.stages?.script?.data;
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="director-storyboard-stage">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">Storyboard</h3>
        <button
          onClick={onGenerate} disabled={busy || !script}
          data-testid="director-generate-storyboard"
          className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          {sb ? "Rebuild" : "Build"} storyboard
        </button>
      </div>
      {!script && <p className="text-sm text-amber-200">Generate a script first.</p>}
      {sb?.shots && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sb.shots.map((s) => (
            <div key={s.id} className="border border-[#1A1A1A] bg-[#0A0A0A] p-3">
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider">Shot {s.id} · {s.duration}s</p>
              <p className="text-xs text-[#F4F4F0] mt-1 line-clamp-2">{s.action}</p>
              <p className="text-[10px] text-[#A0A0A0] mt-2 line-clamp-3 italic">"{s.image_prompt}"</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function KeyframesStage({ project, onRenderShot, busy }) {
  const sb = project.stages?.storyboard?.data;
  if (!sb?.shots) return <p className="text-sm text-amber-200">Build the storyboard first.</p>;
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="director-keyframes-stage">
      <h3 className="font-display text-xl font-bold mb-4">Keyframes</h3>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
        {sb.shots.map((s) => {
          const busyShot = busy === `shot-${s.id}`;
          return (
            <div key={s.id} className="border border-[#1A1A1A] bg-[#0A0A0A] p-3" data-testid={`keyframe-shot-${s.id}`}>
              <p className="text-[10px] text-[#D4AF37] uppercase tracking-wider mb-2">Shot {s.id}</p>
              {s.preview_url ? (
                <img src={`${BACKEND_URL}${s.preview_url}`} alt="" className="w-full aspect-[4/5] object-cover border border-[#1F1F1F] mb-3" />
              ) : (
                <div className="w-full aspect-[4/5] border border-dashed border-[#2A2A2A] flex items-center justify-center mb-3 text-[#5A5A5A] text-xs">
                  No keyframe yet
                </div>
              )}
              <button
                onClick={() => onRenderShot(s.id)}
                disabled={busyShot}
                data-testid={`render-keyframe-${s.id}`}
                className="w-full text-xs px-3 py-2 border border-[#D4AF37]/40 hover:border-[#D4AF37] text-[#D4AF37] disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busyShot ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
                {s.preview_url ? "Re-render" : "Render keyframe"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VoiceoverStage({ project, onGenerate, busy }) {
  const vo = project.stages?.voiceover?.data;
  const script = project.stages?.script?.data;
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="director-voiceover-stage">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">Voiceover</h3>
        <button
          onClick={onGenerate} disabled={busy || !script?.voiceover}
          data-testid="director-generate-voiceover"
          className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
          {vo ? "Re-render VO" : "Render VO"}
        </button>
      </div>
      {!script?.voiceover && <p className="text-sm text-amber-200">No VO script yet — go to Script stage and write one.</p>}
      {script?.voiceover && (
        <p className="text-sm text-[#A0A0A0] italic border border-[#1A1A1A] bg-[#0A0A0A] p-3 mb-4">{script.voiceover}</p>
      )}
      {vo?.preview_url && (
        <audio controls src={`${BACKEND_URL}${vo.preview_url}`} className="w-full" />
      )}
    </div>
  );
}

function AssembleStage({ project, onGenerate, busy }) {
  const a = project.stages?.assemble?.data;
  return (
    <div className="border border-[#2A2A2A] bg-[#0F0F0F] p-6" data-testid="director-assemble-stage">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-xl font-bold">Assemble final manifest</h3>
        <button
          onClick={onGenerate} disabled={busy}
          data-testid="director-assemble"
          className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/5 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Layers className="w-3.5 h-3.5" />}
          {a ? "Re-assemble" : "Assemble"}
        </button>
      </div>
      <p className="text-xs text-[#A0A0A0] mb-3">Collects every stage into a single manifest, ready for the multi-clip editor (server-side ffmpeg stitcher ships in Wave 3).</p>
      {a && (
        <pre className="text-[10px] text-[#A0A0A0] bg-[#0A0A0A] border border-[#1A1A1A] p-4 overflow-auto max-h-[300px]" data-testid="assembled-manifest">
{JSON.stringify(a, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Field({ label, value, onChange, multiline, type = "text", options, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-[#A0A0A0] mb-1.5">{label}</span>
      {multiline ? (
        <textarea value={value} rows={3} onChange={(e) => onChange(e.target.value)}
                  data-testid={testId}
                  className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0] resize-y" />
      ) : type === "select" ? (
        <select value={value} onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
                className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0]">
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)}
               data-testid={testId}
               className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2 text-sm text-[#F4F4F0]" />
      )}
    </label>
  );
}
